import { getCompanyDetailsById } from '@api/company';
import { LoginTypes, verifyInviteTokenRequest } from '@api/login';
import { clearAuthenticationData, logoutAuthenticatedUserV2, redirectToLogin } from '@api/logout';
import { AuthResponse, CompanyDetails } from 'custom';

export const getStoredCompanyPlatformDomain = (): string | null => {
  try {
    const company = localStorage.getItem('company');
    if (!company) {
      return null;
    }

    const parsed = JSON.parse(company) as CompanyDetails;
    const domain = parsed?.platform_domain;

    if (domain && domain !== 'null' && typeof domain === 'string' && domain.trim() !== '') {
      return domain.trim();
    }

    return null;
  } catch {
    return null;
  }
};

export const getCompanyDetails = async () => {
  const company = localStorage.getItem('company');
  const companyDetails: CompanyDetails | null = company && JSON.parse(company);

  if (companyDetails) {
    return companyDetails;
  }

  const user = localStorage.getItem('user');
  const companyId = user && JSON.parse(user).company_id;
  let data: CompanyDetails | null = null;
  if (companyId) {
    data = await getCompanyDetailsById(companyId);
    if (data) {
      localStorage.setItem('company', JSON.stringify(data));
    }
  }

  return data;
};

export const buildCheckYourInboxPath = (
  email: string,
  options?: { companyURL?: string | null; isMobileApp?: boolean },
): string => {
  const companyPath = options?.companyURL ? `/${options.companyURL}` : '';
  const path = `/login/check-your-inbox/${encodeURIComponent(email)}${companyPath}`;

  return options?.isMobileApp ? `${path}?isMobileApp=true` : path;
};

export const buildLoginPath = (options?: {
  companyURL?: string | null;
  isMobileApp?: boolean;
}): string => {
  const path = options?.companyURL ? `/login/${options.companyURL}` : '/login';

  return options?.isMobileApp ? `${path}?isMobileApp=true` : path;
};

/** Route params can pick up a trailing slash from bad `/?query` path builders. */
export const sanitizeLoginEmailParam = (email?: string | null): string => {
  if (!email) {
    return '';
  }

  let decoded = email;
  try {
    decoded = decodeURIComponent(email);
  } catch {
    decoded = email;
  }

  return decoded.replace(/\/+$/, '').trim();
};

export const getTokenVerifiedUser = async (
  loginToken: string,
  type: LoginTypes,
  isNative: boolean,
  email: string | undefined,
  id: string | undefined,
  isDirectLink: boolean,
  redirectedBy: string | null,
  remember: boolean,
) => {
  const sanitizedEmail = email ? sanitizeLoginEmailParam(email) : undefined;
  const body = {
    loginToken,
    type,
    isNative,
    email: sanitizedEmail || undefined,
    id,
    isDirectLink,
    redirectedBy,
    remember,
  };
  const data = (await verifyInviteTokenRequest(body)) as AuthResponse;

  if (!data?.token || !data?.user) {
    const message =
      (data as { message?: string })?.message || 'Invalid authentication. Please try again.';
    throw new Error(message);
  }

  const localRedirectPath = localStorage.getItem('redirectPath');
  const localIsDirectLink = localStorage.getItem('isDirectLink');
  const localRememberToken = localStorage.getItem('rememberMe');

  localStorage.clear();

  if (localRememberToken !== null && localRememberToken !== undefined) {
    localStorage.setItem('rememberMe', localRememberToken.toString());

    // IF REMEMBER ME TRUE CALL COOKIE REFRESH TOKEN (MOBILE AND DESKTOP)
  }

  if (localRedirectPath) {
    localStorage.setItem('redirectPath', localRedirectPath);
  }

  if (localIsDirectLink !== null && localIsDirectLink !== undefined) {
    localStorage.setItem('isDirectLink', localIsDirectLink.toString());
  }

  localStorage.setItem('token', data.token);
  localStorage.setItem('user', JSON.stringify(data.user));
  localStorage.setItem('v', '2.0');

  if (data?.userDetails) {
    localStorage.setItem('userDetails', JSON.stringify(data.userDetails));
  }

  return data;
};

export const logoutUser = async () => {
  const logoutResult = await logoutAuthenticatedUserV2();

  if (logoutResult?.isSuccessful) {
    redirectToLogin();
    await clearAuthenticationData();
  }
};

export interface UserDetailsMemo {
  forename: string;
  surname: string;
}
